// Facilities.jsx
import { useState } from "react";
import { 
  Search, 
  ChevronDown, 
  RotateCcw, 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Building2,
  MapPin,
  Users,
  Cpu,
  Factory,
  CheckCircle,
  Clock,
  Filter,
  Eye
} from "lucide-react";

const FACILITY_TYPES = [
  "All Types",
  "Manufacturing Facility",
  "Innovation Centre",
  "R&D Lab",
  "University Lab",
  "Technology Provider",
  "Testing Centre"
];

const DISTRICTS = [
  "All Districts",
  "Visakhapatnam",
  "Vijayawada",
  "Tirupati",
  "Kurnool",
  "Guntur",
  "Nellore",
  "Anantapur",
  "Kadapa",
  "Srikakulam"
];

const TECHNOLOGIES = [
  "3D Printing",
  "CNC Machining",
  "Laser Cutting",
  "PCB Prototyping",
  "Robotics",
  "Automation",
  "Injection Moulding",
  "Precision Engineering",
  "Aerospace",
  "Electronics",
  "IoT",
  "AI/ML",
  "Composite Materials",
  "Sustainable Manufacturing"
];

const MACHINE_TYPES = [
  "3D Printer",
  "CNC Machine",
  "Laser Cutter",
  "PCB Mill",
  "Robotic Arm",
  "Injection Moulding",
  "Testing Equipment",
  "Scanning Equipment"
];

const PROCESSES = [
  "Additive Manufacturing",
  "Subtractive Manufacturing",
  "Injection Moulding",
  "Laser Processing",
  "PCB Assembly",
  "Robotics & Automation",
  "Quality Testing",
  "Rapid Prototyping"
];

const INDUSTRIES = [
  "Aerospace",
  "Automotive",
  "Biomedical",
  "Consumer Electronics",
  "Defense",
  "Energy",
  "Marine",
  "Medical Devices",
  "Renewable Energy",
  "Textile"
];

const MATERIALS = [
  "Aluminum",
  "Steel",
  "Titanium",
  "Copper",
  "Brass",
  "Plastic (ABS)",
  "Plastic (PLA)",
  "Composite Materials",
  "Carbon Fiber",
  "Glass",
  "Ceramics",
  "Silicone"
];

const CERTIFICATIONS = [
  "ISO 9001",
  "ISO 14001",
  "ISO 13485",
  "AS9100",
  "IATF 16949",
  "NABL",
  "NAAC",
  "FDA Approved",
  "CE Marked"
];

const AVAILABILITY_OPTIONS = ["Available", "Busy", "Maintenance", "By Appointment"];

const STATUS_STYLES = {
  Available: "bg-emerald-50 text-emerald-600",
  Busy: "bg-amber-50 text-amber-600",
  Maintenance: "bg-pink-50 text-pink-600",
  "By Appointment": "bg-blue-50 text-blue-600",
};

const INITIAL_FACILITIES = [
  {
    id: 1,
    name: "APIC Visakhapatnam",
    location: "Visakhapatnam",
    district: "Visakhapatnam",
    facilityType: "Manufacturing Facility",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop&crop=center&auto=format&q=80",
    technologies: ["3D Printing", "CNC Machining", "PCB Prototyping"],
    machineTypes: ["3D Printer", "CNC Machine", "PCB Mill"],
    processes: ["Additive Manufacturing", "Subtractive Manufacturing"],
    industries: ["Aerospace", "Automotive", "Biomedical"],
    materials: ["Aluminum", "Steel", "Plastic (ABS)"],
    certifications: ["ISO 9001", "AS9100"],
    availability: "Available",
    verified: true,
    totalMachines: 42,
    experts: 18
  },
  {
    id: 2,
    name: "APIC Vijayawada",
    location: "Vijayawada",
    district: "Vijayawada",
    facilityType: "Innovation Centre",
    image: "https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=400&h=300&fit=crop&crop=center&auto=format&q=80",
    technologies: ["Robotics", "Automation", "IoT"],
    machineTypes: ["Robotic Arm", "Testing Equipment"],
    processes: ["Robotics & Automation", "Quality Testing"],
    industries: ["Automotive", "Electronics", "Energy"],
    materials: ["Aluminum", "Composite Materials"],
    certifications: ["ISO 14001"],
    availability: "Available",
    verified: true,
    totalMachines: 36,
    experts: 14
  },
  {
    id: 3,
    name: "APIC Tirupati",
    location: "Tirupati",
    district: "Tirupati",
    facilityType: "R&D Lab",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop&crop=center&auto=format&q=80",
    technologies: ["Precision Engineering", "Aerospace", "Composite Materials"],
    machineTypes: ["CNC Machine", "Testing Equipment", "Scanning Equipment"],
    processes: ["Precision Manufacturing", "Quality Testing"],
    industries: ["Aerospace", "Defense", "Medical Devices"],
    materials: ["Titanium", "Aluminum", "Composite Materials"],
    certifications: ["ISO 9001", "ISO 13485", "AS9100"],
    availability: "Available",
    verified: true,
    totalMachines: 29,
    experts: 12
  },
  {
    id: 4,
    name: "APIC Kurnool",
    location: "Kurnool",
    district: "Kurnool",
    facilityType: "Technology Provider",
    image: "https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=400&h=300&fit=crop&crop=center&auto=format&q=80",
    technologies: ["AI/ML", "IoT", "Sustainable Manufacturing"],
    machineTypes: ["Testing Equipment"],
    processes: ["Rapid Prototyping", "Material Processing"],
    industries: ["Energy", "Renewable Energy", "Electronics"],
    materials: ["Copper", "Plastic (PLA)", "Silicone"],
    certifications: ["ISO 14001", "NABL"],
    availability: "By Appointment",
    verified: true,
    totalMachines: 24,
    experts: 9
  }
];

const EMPTY_FORM = {
  name: "",
  location: "",
  district: DISTRICTS[1],
  facilityType: FACILITY_TYPES[1],
  image: "",
  technologies: [],
  machineTypes: [],
  processes: [],
  industries: [],
  materials: [],
  certifications: [],
  availability: AVAILABILITY_OPTIONS[0],
  verified: true,
  totalMachines: "",
  experts: ""
};

export default function Facilities() {
  const [facilities, setFacilities] = useState(INITIAL_FACILITIES);
  const [search, setSearch] = useState("");
  const [facilityType, setFacilityType] = useState("All Types");
  const [district, setDistrict] = useState("All Districts");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const filteredFacilities = facilities.filter((f) => {
    const matchesSearch =
      search.trim() === "" ||
      f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.location.toLowerCase().includes(search.toLowerCase()) ||
      f.district.toLowerCase().includes(search.toLowerCase());
    const matchesType = facilityType === "All Types" || f.facilityType === facilityType;
    const matchesDistrict = district === "All Districts" || f.district === district;
    return matchesSearch && matchesType && matchesDistrict;
  });

  const handleReset = () => {
    setSearch("");
    setFacilityType("All Types");
    setDistrict("All Districts");
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (facility) => {
    setEditingId(facility.id);
    setForm({ 
      ...facility, 
      totalMachines: String(facility.totalMachines), 
      experts: String(facility.experts) 
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setForm((prev) => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter((item) => item !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this facility?")) {
      setFacilities((prev) => prev.filter((f) => f.id !== id));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.location.trim()) return;

    const facilityData = {
      id: editingId || Date.now(),
      name: form.name.trim(),
      location: form.location.trim(),
      district: form.district,
      facilityType: form.facilityType,
      image: form.image || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop&crop=center&auto=format&q=80",
      technologies: form.technologies || [],
      machineTypes: form.machineTypes || [],
      processes: form.processes || [],
      industries: form.industries || [],
      materials: form.materials || [],
      certifications: form.certifications || [],
      availability: form.availability,
      verified: form.verified,
      totalMachines: Number(form.totalMachines) || 0,
      experts: Number(form.experts) || 0
    };

    if (editingId) {
      setFacilities((prev) => prev.map((f) => (f.id === editingId ? { ...f, ...facilityData } : f)));
    } else {
      setFacilities((prev) => [...prev, { ...facilityData, id: Date.now() }]);
    }
    closeModal();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Facilities</h1>
        <p className="mt-1 text-gray-500">
          Manage your facility catalogue — showcase capabilities to innovators
        </p>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, location..."
            className="w-full rounded-full border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-700 shadow-sm outline-none placeholder:text-gray-400 focus:border-blue-400"
          />
        </div>

        <div className="relative">
          <select
            value={facilityType}
            onChange={(e) => setFacilityType(e.target.value)}
            className="appearance-none rounded-full border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-700 shadow-sm outline-none focus:border-blue-400"
          >
            {FACILITY_TYPES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        <div className="relative">
          <select
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            className="appearance-none rounded-full border border-gray-200 bg-white py-3 pl-4 pr-10 text-sm text-gray-700 shadow-sm outline-none focus:border-blue-400"
          >
            {DISTRICTS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        <button
          onClick={handleReset}
          className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <RotateCcw size={16} />
          Reset
        </button>

        <button
          onClick={openAddModal}
          className="flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
        >
          <Plus size={16} />
          Add Facility
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60 text-xs font-semibold uppercase tracking-wider text-gray-400">
              <th className="px-6 py-4">Facility</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4">Location</th>
              <th className="px-6 py-4">District</th>
              <th className="px-6 py-4 text-center">Machines</th>
              <th className="px-6 py-4 text-center">Experts</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFacilities.map((facility) => (
              <tr key={facility.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 text-white">
                      <Building2 size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{facility.name}</p>
                      {facility.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600">
                          <CheckCircle size={10} />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{facility.facilityType}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{facility.location}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{facility.district}</td>
                <td className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  {facility.totalMachines}
                </td>
                <td className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                  {facility.experts}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[facility.availability] || "bg-gray-50 text-gray-600"}`}>
                    {facility.availability}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-3">
                    <button 
                      onClick={() => openEditModal(facility)} 
                      className="text-gray-400 transition hover:text-gray-700" 
                      aria-label={`Edit ${facility.name}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(facility.id)} 
                      className="text-pink-400 transition hover:text-pink-600" 
                      aria-label={`Delete ${facility.name}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {filteredFacilities.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-400">
                  No facilities match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Facility Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeModal}>
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-900">{editingId ? "Edit Facility" : "Add Facility"}</h2>
              <button onClick={closeModal} className="text-gray-400 transition hover:text-gray-700" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Facility Name *</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => handleFormChange("name", e.target.value)}
                      placeholder="e.g. APIC Visakhapatnam"
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Location *</label>
                    <input
                      type="text"
                      required
                      value={form.location}
                      onChange={(e) => handleFormChange("location", e.target.value)}
                      placeholder="e.g. Visakhapatnam"
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">District</label>
                    <select
                      value={form.district}
                      onChange={(e) => handleFormChange("district", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    >
                      {DISTRICTS.filter(d => d !== "All Districts").map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Facility Type</label>
                    <select
                      value={form.facilityType}
                      onChange={(e) => handleFormChange("facilityType", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    >
                      {FACILITY_TYPES.filter(t => t !== "All Types").map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Total Machines</label>
                    <input
                      type="number"
                      min="0"
                      value={form.totalMachines}
                      onChange={(e) => handleFormChange("totalMachines", e.target.value)}
                      placeholder="e.g. 42"
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Number of Experts</label>
                    <input
                      type="number"
                      min="0"
                      value={form.experts}
                      onChange={(e) => handleFormChange("experts", e.target.value)}
                      placeholder="e.g. 18"
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Image URL</label>
                    <input
                      type="text"
                      value={form.image}
                      onChange={(e) => handleFormChange("image", e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Availability</label>
                    <select
                      value={form.availability}
                      onChange={(e) => handleFormChange("availability", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    >
                      {AVAILABILITY_OPTIONS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-gray-500">Verification</label>
                    <select
                      value={form.verified ? "true" : "false"}
                      onChange={(e) => handleFormChange("verified", e.target.value === "true")}
                      className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-400"
                    >
                      <option value="true">Verified</option>
                      <option value="false">Not Verified</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Technologies */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {TECHNOLOGIES.map((tech) => (
                    <button
                      key={tech}
                      type="button"
                      onClick={() => handleMultiSelect("technologies", tech)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.technologies?.includes(tech)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {tech}
                    </button>
                  ))}
                </div>
              </div>

              {/* Machine Types */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Machine Types</h3>
                <div className="flex flex-wrap gap-2">
                  {MACHINE_TYPES.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleMultiSelect("machineTypes", type)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.machineTypes?.includes(type)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Processes */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Processes</h3>
                <div className="flex flex-wrap gap-2">
                  {PROCESSES.map((process) => (
                    <button
                      key={process}
                      type="button"
                      onClick={() => handleMultiSelect("processes", process)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.processes?.includes(process)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {process}
                    </button>
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Industries Served</h3>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES.map((industry) => (
                    <button
                      key={industry}
                      type="button"
                      onClick={() => handleMultiSelect("industries", industry)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.industries?.includes(industry)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {industry}
                    </button>
                  ))}
                </div>
              </div>

              {/* Materials */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Materials</h3>
                <div className="flex flex-wrap gap-2">
                  {MATERIALS.map((material) => (
                    <button
                      key={material}
                      type="button"
                      onClick={() => handleMultiSelect("materials", material)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.materials?.includes(material)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {material}
                    </button>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Certifications</h3>
                <div className="flex flex-wrap gap-2">
                  {CERTIFICATIONS.map((cert) => (
                    <button
                      key={cert}
                      type="button"
                      onClick={() => handleMultiSelect("certifications", cert)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                        form.certifications?.includes(cert)
                          ? "bg-indigo-600 text-white"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {cert}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-5">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                >
                  {editingId ? "Save Changes" : "Add Facility"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}